import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import EnhancedSearchInput from '../common/EnhancedSearchInput';
import MultiSelect from '../common/MultiSelect';
import { useDebouncedCallback } from '../../hooks/useDebounced';

interface BoardToolbarProps {
  filters: {
    search: string;
    agent: string[];
    customerType: string[];
    category: string[];
    dateRange: string[];
    priority: string[];
    status: string[];
  };
  onFiltersChange: (filters: any) => void;
  totalTickets: number;
  filteredTickets: number;
}

const BoardToolbar: React.FC<BoardToolbarProps> = ({
  filters,
  onFiltersChange,
  totalTickets,
  filteredTickets
}) => {
  const { user } = useAuth();
  const [agents, setAgents] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [customerTypes, setCustomerTypes] = useState<any[]>([]);

  // Load dropdown data
  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      const [agentsData, categoriesData, customerTypesData] = await Promise.all([
        apiService.getAgents(),
        apiService.getCategories(),
        apiService.getCustomerTypes()
      ]);

      setAgents(agentsData);
      setCategories(categoriesData);
      setCustomerTypes(customerTypesData);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  // Debounced search handler
  const debouncedSearchChange = useDebouncedCallback(
    (value: string) => {
      onFiltersChange({ search: value });
    },
    300,
    [onFiltersChange]
  );

  const handleSearchChange = (value: string) => {
    debouncedSearchChange(value);
  };

  const handleMultiSelectChange = (key: string, values: string[]) => {
    onFiltersChange({ [key]: values });
  };

  const resetFilters = () => {
    onFiltersChange({
      search: '',
      agent: ['all'],
      customerType: ['all'],
      category: ['all'],
      dateRange: ['all'],
      priority: ['all'],
      status: ['all'] // Reset to show all tickets on board
    });
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'search') return value !== '';
    if (key === 'status') return !Array.isArray(value) || !value.includes('all') || value.length > 1;
    if (Array.isArray(value)) return !value.includes('all') || value.length > 1;
    return false;
  });

  return (
    <div className="bg-white dark:bg-gray-800 px-3 py-2.5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-3">
      {/* Compact Filter Row with Labels */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-start">
        {/* Search Bar - Takes 3 columns */}
        <div className="lg:col-span-3">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            🔍 Search
          </label>
          <EnhancedSearchInput
            value={filters.search}
            onChange={handleSearchChange}
            placeholder="Title, ticket #, company..."
            showSearchTips={false}
            debounceMs={300}
            className="w-full"
          />
        </div>

        {/* Agent Filter */}
        <div className="lg:col-span-1.5">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            👤 Agent
          </label>
          <MultiSelect
            options={[
              { value: 'all', label: 'All Agents' },
              { value: 'unassigned', label: 'Unassigned' },
              ...agents.map((agent) => ({
                value: agent.id,
                label: `${agent.name} ${agent.isOnline ? '🟢' : ''}`
              }))
            ]}
            value={filters.agent}
            onChange={(values) => handleMultiSelectChange('agent', values)}
            placeholder="All"
          />
        </div>

        {/* Customer Type Filter */}
        <div className="lg:col-span-1.5">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            🏢 Customer Type
          </label>
          <MultiSelect
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'Standard', label: 'Standard' },
              { value: 'VIP', label: 'VIP' },
              { value: 'Distributor', label: 'Distributor' },
              ...customerTypes.map((type) => ({
                value: type.name,
                label: type.name
              }))
            ]}
            value={filters.customerType}
            onChange={(values) => handleMultiSelectChange('customerType', values)}
            placeholder="All"
          />
        </div>

        {/* Category Filter */}
        <div className="lg:col-span-1.5">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            🏷️ Category
          </label>
          <MultiSelect
            options={[
              { value: 'all', label: 'All Categories' },
              ...categories.map((category) => ({
                value: category.name,
                label: category.name
              }))
            ]}
            value={filters.category}
            onChange={(values) => handleMultiSelectChange('category', values)}
            placeholder="All"
          />
        </div>

        {/* Date Range Filter */}
        <div className="lg:col-span-1.5">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            📅 Date Range
          </label>
          <MultiSelect
            options={[
              { value: 'all', label: 'All Time' },
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: 'this-week', label: 'This Week' },
              { value: 'last-week', label: 'Last Week' },
              { value: 'this-month', label: 'This Month' },
              { value: 'last-month', label: 'Last Month' }
            ]}
            value={filters.dateRange}
            onChange={(values) => handleMultiSelectChange('dateRange', values)}
            placeholder="All Time"
          />
        </div>

        {/* Priority Filter */}
        <div className="lg:col-span-1.5">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            ⚡ Priority
          </label>
          <MultiSelect
            options={[
              { value: 'all', label: 'All Priorities' },
              { value: 'urgent', label: 'Urgent' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' }
            ]}
            value={filters.priority}
            onChange={(values) => handleMultiSelectChange('priority', values)}
            placeholder="All"
          />
        </div>

        {/* Status Filter */}
        <div className="lg:col-span-2">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            📊 Status
          </label>
          <MultiSelect
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'open', label: 'Open Only' },
              { value: 'closed', label: 'Closed Only' },
              { value: 'new', label: 'New' },
              { value: 'in_progress_ai_agent', label: 'AI Agent' },
              { value: 'escalated-awaiting-agent', label: 'Escalated' },
              { value: 'in_progress_tier1', label: 'Tier 1' },
              { value: 'in_progress_tier2', label: 'Tier 2' },
              { value: 'in_progress_tier3', label: 'Tier 3' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'service', label: 'Service' },
              { value: 'closed-resolved', label: 'Closed - Resolved' },
              { value: 'closed-not-resolved', label: 'Closed - Not Resolved' },
              { value: 'closed-partially-resolved', label: 'Closed - Partial' },
              { value: 'closed-no-feedback', label: 'Closed - No Feedback' },
              { value: 'closed-by-agent-resolved', label: 'Closed by Agent' },
              { value: 'abandoned', label: 'Abandoned' }
            ]}
            value={filters.status}
            onChange={(values) => handleMultiSelectChange('status', values)}
            placeholder="All Statuses"
          />
        </div>
      </div>

      {/* Compact Filter Summary */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium text-primary-600 dark:text-primary-400">
              {filteredTickets}
            </span>
            {' '}of{' '}
            <span className="font-medium">{totalTickets}</span>
            {' '}tickets
          </div>
          
          <button
            onClick={resetFilters}
            className="inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default BoardToolbar;
